import React, { useState, useEffect } from 'react';
import { subscribeToOrder } from '../services/orderService';

const STATUS_META = {
  pending: {
    title: 'Order Placed',
    description: 'We received your order and will confirm it shortly.',
    icon: '🧾',
    className: 'pending',
  },
  confirmed: {
    title: 'Confirmed',
    description: 'Your order is confirmed and being prepared.',
    icon: '✅',
    className: 'confirmed',
  },
  'out-for-delivery': {
    title: 'Out for Delivery',
    description: 'Delivery partner is on the way to your location.',
    icon: '🛵',
    className: 'out-for-delivery',
  },
  delivered: {
    title: 'Delivered',
    description: 'Order delivered successfully.',
    icon: '🎉',
    className: 'delivered',
  },
  cancelled: {
    title: 'Cancelled',
    description: 'This order was cancelled.',
    icon: '❌',
    className: 'cancelled',
  },
};

const STATUS_FLOW = ['pending', 'confirmed', 'out-for-delivery', 'delivered'];

// const getDateFromValue = (value) => {
//   if (!value) return null;
//   if (value?.toDate) return value.toDate();
//   if (value instanceof Date) return value;
//   if (typeof value === 'string' || typeof value === 'number') {
//     const parsed = new Date(value);
//     return Number.isNaN(parsed.getTime()) ? null : parsed;
//   }
//   return null;
// };

function OrderTracking({ order, onClose, isHomePage = false }) {
  const [liveOrder, setLiveOrder] = useState(order);

  useEffect(() => {
    setLiveOrder(order);
  }, [order]);

  useEffect(() => {
    if (!isHomePage) {
      const unsubscribe = subscribeToOrder(order.id, (updatedOrder) => {
        if (updatedOrder) {
          setLiveOrder(updatedOrder);
        }
      });

      return () => unsubscribe();
    }
  }, [order.id, isHomePage]);

  const currentStatus = liveOrder.status || 'pending';
  const currentStatusMeta = STATUS_META[currentStatus] || STATUS_META.pending;
  const assignedAgentName = liveOrder.assignedAgentName || '';
  const assignedAgentPhone = liveOrder.assignedAgentPhone || '';
  const currentStepIndex = STATUS_FLOW.indexOf(currentStatus);
  // const sortedHistory = [...(liveOrder.statusHistory || [])]
  //   .map((entry) => ({
  //     ...entry,
  //     parsedTime: getDateFromValue(entry.timestamp),
  //   }))
  //   .sort((a, b) => (a.parsedTime?.getTime?.() || 0) - (b.parsedTime?.getTime?.() || 0));

  if (isHomePage) {
    return null; // Do not render the banner on the home page
  }

  return (
    <div className="order-tracking" onClick={onClose}>
      <div className="tracking-container" onClick={(e) => e.stopPropagation()}>
        <div className="tracking-header">
          <h2>Track Order</h2>
          <button className="close-btn" type="button" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="tracking-content" style={{ position: 'relative', zIndex: 1 }}>
        {/* Order Info */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))',
          gap: '12px',
          padding: '18px',
          background: 'linear-gradient(135deg, rgba(255,248,243,0.98) 0%, rgba(255,237,213,0.96) 50%, rgba(254,249,195,0.92) 100%)',
          borderRadius: '22px',
          marginBottom: '22px',
          zIndex: 2,
          border: '1px solid rgba(255, 214, 191, 0.9)',
          boxShadow: '0 18px 38px rgba(255, 90, 31, 0.10)',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}>
          <div style={{ minWidth: 0 }}>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Order ID</span>
            <span style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>#{liveOrder.id.substring(0, 8).toUpperCase()}</span>
          </div>
          <div style={{ textAlign: 'right', minWidth: 0 }}>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Order Date</span>
            <span style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text-primary)' }}>
              {liveOrder.createdAt?.toDate?.()?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) || 'N/A'}
            </span>
          </div>
        </div>

        {/* Current Status */}
        <div style={{ marginBottom: '24px', width: '100%', maxWidth: '100%' }}>
          <div style={{
            background: currentStatusMeta.className === 'delivered' ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' :
                       currentStatusMeta.className === 'out-for-delivery' ? 'linear-gradient(135deg, #fb923c 0%, #f97316 100%)' :
                       currentStatusMeta.className === 'confirmed' ? 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)' :
                       currentStatusMeta.className === 'cancelled' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                       'linear-gradient(135deg, #ff9a5a 0%, #ff6b35 100%)',
            borderRadius: '26px',
            padding: '26px',
            color: 'white',
            boxShadow: '0 22px 42px rgba(255, 90, 31, 0.22)',
            position: 'relative',
            overflow: 'hidden',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}>
            <div style={{
              position: 'absolute',
              top: '-28px',
              right: '-18px',
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.24), rgba(255,255,255,0) 70%)',
              pointerEvents: 'none',
            }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px', position: 'relative', zIndex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '48px', flexShrink: 0 }}>{currentStatusMeta.icon}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ margin: '0 0 4px', fontSize: '20px', fontWeight: '700' }}>{currentStatusMeta.title}</h3>
                <p style={{ margin: 0, fontSize: '14px', opacity: 0.95 }}>{currentStatusMeta.description}</p>
              </div>
            </div>
            {currentStatus === 'out-for-delivery' && assignedAgentName && (
              <div style={{
                background: 'rgba(255, 255, 255, 0.18)',
                borderRadius: '18px',
                padding: '13px 16px',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.22)',
                position: 'relative',
                zIndex: 1,
              }}>
                <div style={{ fontSize: '13px', opacity: 0.9, marginBottom: '4px' }}>Delivery Partner</div>
                <div style={{ fontSize: '16px', fontWeight: '600' }}>{assignedAgentName} is on the way</div>
              </div>
            )}
          </div>
        </div>

        {/* Removed map and improved UI styling for Status Updates */}
        <div style={{
          marginBottom: '24px',
          padding: '24px 20px',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.98) 100%)',
          borderRadius: '24px',
          border: '1px solid rgba(226,232,240,0.95)',
          boxShadow: '0 18px 34px rgba(15, 23, 42, 0.07)',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {STATUS_FLOW.map((status, index) => {
              const meta = STATUS_META[status];
              const isDone = currentStepIndex > index;
              const isCurrent = currentStepIndex === index;

              return (
                <div key={status} style={{ display: 'flex', alignItems: 'center', gap: '16px', minWidth: 0 }}>
                  {/* Status Icon */}
                  <div style={{
                    width: '46px', height: '46px', borderRadius: '50%',
                    background: isDone ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' : isCurrent ? 'linear-gradient(135deg, #ff9a5a 0%, #ff6b35 100%)' : '#e2e8f0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'white', fontWeight: 'bold',
                    boxShadow: isCurrent ? '0 14px 24px rgba(255, 90, 31, 0.28)' : isDone ? '0 10px 18px rgba(34, 197, 94, 0.2)' : 'none',
                    flexShrink: 0,
                  }}>
                    {isDone ? '✓' : meta.icon}
                  </div>

                  {/* Status Details */}
                  <div style={{ flex: 1, minWidth: 0, padding: '12px 14px', borderRadius: '18px', background: isCurrent ? 'rgba(255, 247, 237, 0.9)' : 'rgba(248, 250, 252, 0.95)', border: '1px solid rgba(226,232,240,0.85)', boxSizing: 'border-box' }}>
                    <div style={{ fontSize: '16px', fontWeight: '700', color: isCurrent ? '#ff5a1f' : '#0f172a' }}>
                      {meta.title}
                    </div>
                    <div style={{ fontSize: '14px', color: '#64748b' }}>
                      {meta.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.98) 100%)',
          borderRadius: '22px',
          border: '1px solid rgba(226,232,240,0.95)',
          padding: '18px',
          marginBottom: '16px',
          boxShadow: '0 16px 30px rgba(15, 23, 42, 0.06)',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box'
        }}>
          <h4 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>Delivery Agent</h4>
          {assignedAgentName ? (
            <div style={{ background: 'linear-gradient(180deg, rgba(248,250,252,0.96) 0%, rgba(255,255,255,0.96) 100%)', padding: '14px', borderRadius: '18px', border: '1px solid rgba(226,232,240,0.9)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div>
                  <p style={{ margin: '0 0 4px', fontWeight: '600', fontSize: '15px' }}>
                    {assignedAgentName}
                  </p>
                  <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)' }}>
                    📞 {assignedAgentPhone || 'Phone not available'}
                  </p>
                </div>
                {assignedAgentPhone && (
                  <a
                    href={`tel:${assignedAgentPhone}`}
                    style={{
                      padding: '10px 20px',
                      background: 'linear-gradient(135deg, #ff9a5a 0%, #ff6b35 100%)',
                      color: 'white',
                      borderRadius: '16px',
                      textDecoration: 'none',
                      fontSize: '14px',
                      fontWeight: '600',
                      boxShadow: '0 12px 24px rgba(255, 90, 31, 0.24)',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 14px 26px rgba(255, 90, 31, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 12px 24px rgba(255, 90, 31, 0.24)';
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>📞</span> Call
                  </a>
                )}
              </div>
            </div>
          ) : (
            <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-muted)' }}>Agent not assigned yet.</p>
          )}
        </div>

        <div style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.98) 100%)',
          borderRadius: '22px',
          border: '1px solid rgba(226,232,240,0.95)',
          padding: '18px',
          marginBottom: '16px',
          boxShadow: '0 16px 30px rgba(15, 23, 42, 0.06)'
        }}>
          <h4 style={{ margin: '0 0 12px', fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>Payment</h4>
          <div style={{ background: 'var(--gray-50)', padding: '12px', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Amount to pay on delivery</span>
              <span style={{ fontSize: '20px', fontWeight: '700', color: 'var(--primary)' }}>₹{liveOrder.total || 0}</span>
            </div>
            <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
              Mode: Cash / UPI
            </div>
          </div>
          {liveOrder.paymentProof && (
            <div style={{ marginTop: 12 }}>
              <p style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>
                Payment Proof:
              </p>
              <img
                src={liveOrder.paymentProof}
                alt="Payment Proof"
                style={{
                  maxWidth: '100%',
                  maxHeight: 200,
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  cursor: 'pointer',
                }}
                onClick={() => window.open(liveOrder.paymentProof, '_blank')}
              />
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Click to view full size
              </p>
            </div>
          )}
          <div style={{
            marginTop: '12px',
            padding: '8px 12px',
            borderRadius: '6px',
            borderLeft: '3px solid #f59e0b',
            background: '#fffbeb',
            fontSize: '12px',
            color: '#92400e'
          }}>
            If you find any damage during delivery, please report it to the rider immediately.
          </div>
        </div>

        {/* Order Items */}
        <div className="order-detail-section">
          <h4>Order Items</h4>
          {liveOrder.items && liveOrder.items.length > 0 ? (
            <div className="order-items-summary">
              {liveOrder.items.map((item, idx) => (
                <div key={idx} className="item-summary">
                  <span className="item-name">{item.name}</span>
                  <span className="item-qty">× {item.quantity}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted">No items</p>
          )}
        </div>

        {/* Delivery Address */}
        <div className="order-detail-section">
          <h4>Delivery Address</h4>
          {liveOrder.deliveryAddress ? (
            <div className="delivery-address-info">
              <p className="address-label">
                <strong>{liveOrder.deliveryAddress.label}</strong>
              </p>
              <p className="address-text">
                {[
                  liveOrder.deliveryAddress.line1,
                  liveOrder.deliveryAddress.line2,
                  liveOrder.deliveryAddress.city,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </p>
              <p className="address-pincode">
                Pincode: {liveOrder.deliveryAddress.pincode}
              </p>
            </div>
          ) : (
            <p className="muted">No address provided</p>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}

export default OrderTracking;
